import {
  toVehicleCostEmployee,
  VEHICLE_COST_HR_MISSING_MESSAGE,
} from '@/utils/approval/vehicleCost';
import { formatAsDate } from '@/utils/format';
import { localize } from '@/utils/approval/localize';

/** 신규입사자 법인카드 신청 — 레거시 appr_edit_forms_new_card */
export const NEW_CORPORATE_CARD_TYPE = 204;

export const isNewCorporateCardType = (apvType: number): boolean =>
  apvType === NEW_CORPORATE_CARD_TYPE;

/** 레거시 이용한도 — 값은 코드, 문구는 언어와 무관하게 한국어 원문 */
export const NEW_CARD_CREDIT_LIMITS = [
  { value: 'limit_500', label: '팀원 30만원' },
  {
    value: 'limit_1000',
    label: '팀원 100만원(마케팅/영업/카테고리운영/VMD팀/통합인테리어팀)',
  },
  { value: 'limit_1500', label: '팀장/파트장 200만원' },
  { value: 'limit_2000', label: '임원/사업부장 300만원' },
] as const;

export const NEW_CARD_DEFAULT_CREDIT_LIMIT = 'limit_500';

/** 레거시 카드 종류 — 표시 문구가 곧 저장값 */
export const NEW_CARD_TYPES = ['비자(VISA)', '유니온페이(UNION PAY)'] as const;

export const NEW_CARD_CREDIT_INFO_URL =
  'https://biz.kbcard.com/CXERCCAS0001.cms';

export type NewCardDocumentKind = 'idCard' | 'bankBook';

export type NewCardVerifiedFile = {
  uuid: string;
  fileName: string;
  /** AI 확인 통과 */
  verified: boolean;
  /** 업로드·AI 확인 진행 중 */
  processing: boolean;
};

export type NewCardApplicant = {
  name: string;
  employeeNumber: string;
  /** 법인·사업부·부서를 공백으로 이은 값 */
  department: string;
  email: string;
  hireDate: string;
};

export type NewCorporateCardFormState = {
  applicant: NewCardApplicant;
  idCard: NewCardVerifiedFile | null;
  bankBook: NewCardVerifiedFile | null;
  creditInfoConsent: boolean;
  enName: string;
  enLastname: string;
  phoneNumber: string;
  creditLimit: string;
  cardType: string;
  pledgeAgree: 'Y' | 'N' | '';
};

/** 저장 attributes — 스웨거 CorporateCardNewEmployeeApplicationAttributes */
export type NewCorporateCardAttributes = {
  content: string;
  employeeName: string;
  employeeNumber: string;
  department: string | null;
  email: string;
  hireDate: string;
  creditInfoConsent: boolean;
  pledgeAgree: boolean;
  idCardFilename: string | null;
  idCardUuid: string | null;
  idCardUrl: string | null;
  bankBookFilename: string | null;
  bankBookUuid: string | null;
  bankBookUrl: string | null;
  enName: string | null;
  enLastname: string | null;
  phoneNumber: string | null;
  creditLimit: string | null;
  cardType: string | null;
};

/** 레거시 formatPhoneNumber — 숫자만 남기고 하이픈 자동 삽입(02 지역번호 별도) */
export const formatNewCardPhoneNumber = (value: string): string => {
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('02')) {
    digits = digits.slice(0, 10);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    }
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  digits = digits.slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

/** 레거시 영문 이름 입력 — 영문·공백만, 대문자 */
export const sanitizeNewCardEnglishName = (value: string): string =>
  value.replace(/[^a-zA-Z ]/g, '').toUpperCase();

export const buildNewCorporateCardSubject = (name: string): string =>
  localize('법인카드 신규발급 신청의 건 ({{name}})', { name });

export const createNewCorporateCardState = (
  applicant: NewCardApplicant,
  phoneNumber: string,
): NewCorporateCardFormState => ({
  applicant,
  idCard: null,
  bankBook: null,
  creditInfoConsent: false,
  enName: '',
  enLastname: '',
  phoneNumber: formatNewCardPhoneNumber(phoneNumber),
  creditLimit: NEW_CARD_DEFAULT_CREDIT_LIMIT,
  cardType: '',
  pledgeAgree: '',
});

/** 내 인사 정보로 새 작성 상태. 인사 정보(사번)가 없으면 null */
export const newCorporateCardStateFromMyInfo = (
  info:
    | (Parameters<typeof toVehicleCostEmployee>[0] & {
        email?: string | null;
        phoneMobile?: string | null;
      })
    | null
    | undefined,
): NewCorporateCardFormState | null => {
  const employee = toVehicleCostEmployee(info);
  if (!employee) return null;
  return createNewCorporateCardState(
    {
      name: employee.name,
      employeeNumber: employee.employeeNumber,
      department: employee.affiliation,
      email: info?.email ?? '',
      hireDate: employee.enterDate,
    },
    info?.phoneMobile ?? '',
  );
};

/** 이탈 경고 판정용 — 사용자가 고칠 수 있는 값만(신청자 정보는 자동) */
export const newCorporateCardKey = (
  state: NewCorporateCardFormState | null,
): string =>
  state
    ? JSON.stringify([
        state.idCard?.uuid ?? '',
        state.idCard?.verified ?? false,
        state.bankBook?.uuid ?? '',
        state.bankBook?.verified ?? false,
        state.creditInfoConsent,
        state.enName,
        state.enLastname,
        state.phoneNumber,
        state.creditLimit,
        state.cardType,
        state.pledgeAgree,
      ])
    : '';

/** 레거시 save_doc_new_card_approval 검사 순서·문구 그대로(임시저장도 같은 검사) */
export const checkNewCorporateCardBeforeSave = (
  state: NewCorporateCardFormState | null | undefined,
): string | null => {
  if (!state) return VEHICLE_COST_HR_MISSING_MESSAGE;
  const { idCard, bankBook } = state;
  if (!idCard?.uuid) return '신분증을 첨부해주세요.';
  if (idCard.processing) {
    return '신분증 AI 검증이 진행 중입니다. 완료 후 다시 시도해주세요.';
  }
  if (!idCard.verified) {
    return '신분증 AI 검증을 통과해야 합니다. 파일을 다시 첨부해주세요.';
  }
  if (!bankBook?.uuid) return '급여 통장사본을 첨부해주세요.';
  if (bankBook.processing) {
    return '통장사본 AI 검증이 진행 중입니다. 완료 후 다시 시도해주세요.';
  }
  if (!bankBook.verified) {
    return '통장사본 AI 검증을 통과해야 합니다. 파일을 다시 첨부해주세요.';
  }
  if (!state.creditInfoConsent) return '신용 정보 조회에 동의해주세요.';
  if (!state.enName.trim() || !state.enLastname.trim()) {
    return '영문 이름을 입력해주세요.';
  }
  if (!state.phoneNumber.trim()) return 'SMS 수신 번호를 입력해주세요.';
  if (!state.creditLimit) return '이용한도를 선택해주세요.';
  if (!state.cardType) return '카드 종류를 선택해주세요.';
  if (state.pledgeAgree !== 'Y') return '개인형 법인카드 서약에 동의해주세요.';
  return null;
};

export const buildNewCorporateCardAttributes = (
  state: NewCorporateCardFormState | null | undefined,
  content: string,
): NewCorporateCardAttributes | null => {
  if (!state) return null;
  const { applicant, idCard, bankBook } = state;
  return {
    content,
    employeeName: applicant.name,
    employeeNumber: applicant.employeeNumber,
    department: applicant.department || null,
    email: applicant.email,
    hireDate: applicant.hireDate,
    creditInfoConsent: state.creditInfoConsent,
    pledgeAgree: state.pledgeAgree === 'Y',
    idCardFilename: idCard?.fileName || null,
    idCardUuid: idCard?.uuid || null,
    idCardUrl: null,
    bankBookFilename: bankBook?.fileName || null,
    bankBookUuid: bankBook?.uuid || null,
    bankBookUrl: null,
    enName: state.enName.trim() || null,
    enLastname: state.enLastname.trim() || null,
    phoneNumber: state.phoneNumber.trim() || null,
    creditLimit: state.creditLimit || null,
    cardType: state.cardType || null,
  };
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** 단건조회 attributes. 사번이 없으면 null */
export const parseNewCorporateCardAttributes = (
  attributes: unknown,
): NewCorporateCardAttributes | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const a = attributes as Record<string, unknown>;
  if (!str(a.employeeNumber)) return null;
  const orNull = (v: unknown) => str(v) || null;
  return {
    content: str(a.content),
    employeeName: str(a.employeeName),
    employeeNumber: str(a.employeeNumber),
    department: orNull(a.department),
    email: str(a.email),
    // 백엔드가 UTC 시각(…T15:00:00.000Z)으로 돌려줌 — 잘라 쓰면 하루 밀림
    hireDate: str(a.hireDate) ? formatAsDate(str(a.hireDate)) : '',
    creditInfoConsent: a.creditInfoConsent === true,
    pledgeAgree: a.pledgeAgree === true,
    idCardFilename: orNull(a.idCardFilename),
    idCardUuid: orNull(a.idCardUuid),
    idCardUrl: orNull(a.idCardUrl),
    bankBookFilename: orNull(a.bankBookFilename),
    bankBookUuid: orNull(a.bankBookUuid),
    bankBookUrl: orNull(a.bankBookUrl),
    enName: orNull(a.enName),
    enLastname: orNull(a.enLastname),
    phoneNumber: orNull(a.phoneNumber),
    creditLimit: orNull(a.creditLimit),
    cardType: orNull(a.cardType),
  };
};

const savedFile = (
  uuid: string | null,
  fileName: string | null,
): NewCardVerifiedFile | null =>
  uuid
    ? { uuid, fileName: fileName ?? '', verified: true, processing: false }
    : null;

/** 편집 진입 — 레거시 restoreEditModeData 처럼 저장 파일은 확인 완료로 복원 */
export const newCorporateCardAttributesToState = (
  saved: NewCorporateCardAttributes,
): NewCorporateCardFormState => ({
  applicant: {
    name: saved.employeeName,
    employeeNumber: saved.employeeNumber,
    department: saved.department ?? '',
    email: saved.email,
    hireDate: saved.hireDate,
  },
  idCard: savedFile(saved.idCardUuid, saved.idCardFilename),
  bankBook: savedFile(saved.bankBookUuid, saved.bankBookFilename),
  creditInfoConsent: saved.creditInfoConsent,
  enName: saved.enName ?? '',
  enLastname: saved.enLastname ?? '',
  phoneNumber: saved.phoneNumber ?? '',
  creditLimit: saved.creditLimit ?? NEW_CARD_DEFAULT_CREDIT_LIMIT,
  cardType: saved.cardType ?? '',
  pledgeAgree: saved.pledgeAgree ? 'Y' : '',
});

export const newCardCreditLimitLabel = (value: string | null): string =>
  NEW_CARD_CREDIT_LIMITS.find((o) => o.value === value)?.label ?? value ?? '';
